// Fix FP (2026-10-09): "si yo estoy en el 220 (…) que me genere la siguiente factura que ahora
// voy a hacer, la 221, me genere con todos los datos de la factura y que de ahí yo copio" (CEN,
// reunión de Finanzas). Numeración correlativa de las facturas de venta (sin conexión con la
// DNIT): la numeración, el alta de la factura de una venta y la factura para transcribir al
// facturador (en pantalla, copiar los datos y PDF).
import { useState, useEffect } from "react";
import { X, Loader2, Check, Copy, Eye, Download } from "lucide-react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { API } from "../contexts/AuthContext";
import { todayAsuncion } from "../utils/format";
import { fechaReporte, verPdf } from "../utils/reportes";
import { guardarPDF } from "../pages/compras/helpers";

const gs = (n) => Math.round(Number(n) || 0).toLocaleString("es-PY");
const condTxt = (c) => (c === "contado" ? "Contado" : c === "credito" ? "Crédito" : c || "—");

function Modal({ titulo, onClose, testId, children, ancho = "max-w-lg" }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid={testId}>
      <div className={`bg-white rounded-2xl shadow-xl w-full ${ancho} p-5 space-y-4 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{titulo}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const campo = "block border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-full mt-1";

// ── Numeración: establecimiento, punto de expedición, timbrado y próximo número ──
export function NumeracionModal({ cfg, puedeEditar, onClose, onSaved }) {
  const [f, setF] = useState({ ...cfg });
  const [guardando, setGuardando] = useState(false);
  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await axios.put(`${API}/contabilidad/facturacion`,
        { ...f, proximo_numero: Number(String(f.proximo_numero).replace(/\D/g, "")) || 0 }, { withCredentials: true });
      toast.success(`Numeración guardada. Próxima factura: ${data.proximo}`);
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setGuardando(false); }
  };
  const input = (k, l, extra = {}) => (
    <label className="text-xs font-semibold text-gray-500">{l}
      <input value={f[k] ?? ""} onChange={e => setF(x => ({ ...x, [k]: e.target.value }))} disabled={!puedeEditar}
        className={campo} data-testid={`fact-cfg-${k}`} {...extra} />
    </label>
  );
  return (
    <Modal titulo="Numeración de facturas" onClose={onClose} testId="fact-cfg-modal">
      <p className="text-xs text-gray-500">El sistema asigna a cada factura el número siguiente. Cargá el timbrado vigente y el número de la próxima factura que vas a hacer.</p>
      <div className="grid grid-cols-2 gap-3">
        {input("establecimiento", "Establecimiento", { maxLength: 3, placeholder: "001" })}
        {input("punto_expedicion", "Punto de expedición", { maxLength: 3, placeholder: "001" })}
        {input("timbrado", "Timbrado", { inputMode: "numeric" })}
        {input("proximo_numero", "Próximo número", { inputMode: "numeric" })}
        {input("inicio_vigencia", "Vigencia desde", { type: "date" })}
        {input("fin_vigencia", "Vigencia hasta", { type: "date" })}
      </div>
      <p className="text-sm text-gray-700" data-testid="fact-cfg-proximo">
        Próxima factura: <strong className="font-mono">{`${f.establecimiento || "001"}-${f.punto_expedicion || "001"}-${String(Number(String(f.proximo_numero).replace(/\D/g, "")) || 0).padStart(7, "0")}`}</strong>
      </p>
      {puedeEditar && (
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50">Cancelar</button>
          <button onClick={guardar} disabled={guardando} data-testid="fact-cfg-guardar"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#2E6DA4] text-white hover:bg-[#245A8A] disabled:opacity-50">
            {guardando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Guardar
          </button>
        </div>
      )}
    </Modal>
  );
}

// ── Emitir: asigna el número siguiente a una venta sin factura ──
export function EmitirFacturaModal({ fila, cfg, onClose, onEmitida }) {
  const [f, setF] = useState({ ruc: fila.ruc || "", razon_social: fila.razon_social || "", condicion: fila.condicion || "", fecha: todayAsuncion() });
  const [emitiendo, setEmitiendo] = useState(false);
  const emitir = async () => {
    if (!f.ruc.trim()) { toast.error("Cargá el RUC o C.I. del cliente"); return; }
    setEmitiendo(true);
    try {
      const { data } = await axios.post(`${API}/contabilidad/libro-ventas/${fila._id}/emitir-factura`, f, { withCredentials: true });
      toast.success(`Factura ${data.nro_factura} emitida`);
      onEmitida(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo emitir la factura"); }
    finally { setEmitiendo(false); }
  };
  return (
    <Modal titulo="Emitir factura" onClose={onClose} testId="fact-emitir-modal">
      <div className="bg-slate-50 rounded-xl px-4 py-3 text-sm space-y-1">
        <p>Factura N° <strong className="font-mono" data-testid="fact-emitir-nro">{cfg.proximo}</strong> · Timbrado {cfg.timbrado}</p>
        <p className="text-gray-600">{fila.concepto || "—"}{fila.obra ? ` · ${fila.obra}` : ""}</p>
        <p className="text-gray-900 font-semibold">Total Gs. {gs(fila.total)}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-gray-500 col-span-2">Razón social del cliente
          <input value={f.razon_social} onChange={e => setF(x => ({ ...x, razon_social: e.target.value }))} className={campo} data-testid="fact-emitir-razon_social" />
        </label>
        <label className="text-xs font-semibold text-gray-500">RUC / C.I.
          <input value={f.ruc} onChange={e => setF(x => ({ ...x, ruc: e.target.value }))} className={campo} data-testid="fact-emitir-ruc" />
        </label>
        <label className="text-xs font-semibold text-gray-500">Fecha de la factura
          <input type="date" value={f.fecha} onChange={e => setF(x => ({ ...x, fecha: e.target.value }))} className={campo} data-testid="fact-emitir-fecha" />
        </label>
        <label className="text-xs font-semibold text-gray-500">Condición de venta
          <select value={f.condicion} onChange={e => setF(x => ({ ...x, condicion: e.target.value }))} className={campo} data-testid="fact-emitir-condicion">
            <option value="">—</option>
            <option value="contado">Contado</option>
            <option value="credito">Crédito</option>
          </select>
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50">Cancelar</button>
        <button onClick={emitir} disabled={emitiendo} data-testid="fact-emitir-confirmar"
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
          {emitiendo ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Emitir factura {cfg.proximo}
        </button>
      </div>
    </Modal>
  );
}

// ── La factura para transcribir: datos en pantalla, copiar y PDF ──
function datosFactura(fila, empresa) {
  const usd = fila.moneda === "USD";
  return [
    ["Emisor", `${empresa.razon_social || ""}${empresa.ruc ? ` · RUC ${empresa.ruc}` : ""}`],
    ["Factura N°", fila.nro_factura],
    ["Timbrado", `${fila.timbrado || ""}${fila.inicio_timbrado ? ` · vigente desde ${fechaReporte(fila.inicio_timbrado)}` : ""}${fila.venc_timbrado ? ` hasta ${fechaReporte(fila.venc_timbrado)}` : ""}`],
    ["Fecha de emisión", fechaReporte(fila.fecha)],
    ["Condición de venta", condTxt(fila.condicion)],
    ["Cliente", fila.razon_social],
    ["RUC / C.I.", fila.ruc || "—"],
    ["Descripción", [fila.concepto, fila.obra].filter(Boolean).join(" · ")],
    ...(usd ? [["Moneda", `US$ ${Number(fila.total_usd || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · 1 USD = Gs. ${gs(fila.tipo_cambio)}`]] : []),
    ["Exentas", `Gs. ${gs(fila.exento)}`],
    ["Gravadas 10%", `Gs. ${gs(fila.grav_10 + fila.iva_10)}`],
    ["Liquidación IVA 10%", `Gs. ${gs(fila.iva_10)}`],
    ["Total a pagar", `Gs. ${gs(fila.total)}`],
  ];
}

export function facturaPdf(fila, empresa, ver = false) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(13);
  doc.text(empresa.razon_social || "", 14, 16);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  doc.text([empresa.descripcion, empresa.ruc && `RUC ${empresa.ruc}`, empresa.direccion, empresa.telefono].filter(Boolean).join(" · "), 14, 21, { maxWidth: 110 });
  doc.setDrawColor(46, 109, 164); doc.rect(130, 9, 66, 24);
  doc.setFontSize(8);
  doc.text(`Timbrado N° ${fila.timbrado || ""}`, 133, 14);
  if (fila.inicio_timbrado) doc.text(`Vigencia desde ${fechaReporte(fila.inicio_timbrado)}`, 133, 18);
  if (fila.venc_timbrado) doc.text(`Vigencia hasta ${fechaReporte(fila.venc_timbrado)}`, 133, 22);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("FACTURA", 133, 27); doc.text(fila.nro_factura || "", 155, 27);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  autoTable(doc, {
    startY: 38, theme: "plain", styles: { fontSize: 9, cellPadding: 1.2 },
    body: [["Fecha de emisión:", fechaReporte(fila.fecha), "Condición de venta:", condTxt(fila.condicion)],
      ["Nombre o razón social:", fila.razon_social || "", "RUC / C.I.:", fila.ruc || ""]],
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 40 }, 2: { fontStyle: "bold", cellWidth: 35 } },
  });
  const desc = [fila.concepto, fila.obra].filter(Boolean).join(" · ");
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 3,
    head: [["Cant.", "Descripción", "Precio unitario", "Exentas", "5%", "10%"]],
    body: [["1", desc, gs(fila.total), fila.exento ? gs(fila.exento) : "", "", fila.grav_10 ? gs(fila.grav_10 + fila.iva_10) : ""]],
    foot: [["", "Subtotales", "", gs(fila.exento), "0", gs(fila.grav_10 + fila.iva_10)],
      ["", "Total a pagar", "", "", "", gs(fila.total)],
      ["", `Liquidación del IVA:  (5%) 0  ·  (10%) ${gs(fila.iva_10)}  ·  Total IVA ${gs(fila.iva_10)}`, "", "", "", ""]],
    styles: { fontSize: 8.5, cellPadding: 1.8 },
    headStyles: { fillColor: [46, 109, 164] },
    footStyles: { fillColor: [235, 242, 250], textColor: [30, 30, 30] },
    columnStyles: { 0: { cellWidth: 12 }, 2: { halign: "right", cellWidth: 28 }, 3: { halign: "right", cellWidth: 24 }, 4: { halign: "right", cellWidth: 16 }, 5: { halign: "right", cellWidth: 28 } },
  });
  let y = doc.lastAutoTable.finalY + 6;
  if (fila.moneda === "USD") {
    doc.text(`Moneda de la operación: US$ ${Number(fila.total_usd || 0).toLocaleString("es-PY", { minimumFractionDigits: 2 })} · Tipo de cambio Gs. ${gs(fila.tipo_cambio)}`, 14, y);
    y += 6;
  }
  doc.setFontSize(8); doc.setTextColor(180, 40, 40);
  doc.text("Borrador para transcribir al facturador. No es un comprobante válido.", 14, y);
  const archivo = `Factura_${fila.nro_factura || "borrador"}.pdf`;
  if (ver) verPdf(doc, archivo); else guardarPDF(doc, archivo);
}

export function FacturaVistaModal({ fila, onClose }) {
  const [empresa, setEmpresa] = useState(null);
  useEffect(() => {
    axios.get(`${API}/settings/empresa`, { withCredentials: true }).then(r => setEmpresa(r.data)).catch(() => setEmpresa({}));
  }, []);
  if (!empresa) return null;
  const datos = datosFactura(fila, empresa);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(datos.map(([k, v]) => `${k}: ${v}`).join("\n"));
      toast.success("Datos copiados");
    } catch { toast.error("No se pudo copiar: seleccioná el texto a mano"); }
  };
  return (
    <Modal titulo={`Factura ${fila.nro_factura}`} onClose={onClose} testId="fact-vista-modal" ancho="max-w-xl">
      <table className="w-full text-sm" data-testid="fact-vista-datos">
        <tbody>
          {datos.map(([k, v]) => (
            <tr key={k} className="border-b border-gray-50">
              <td className="py-1.5 pr-3 text-xs font-semibold text-gray-500 whitespace-nowrap align-top">{k}</td>
              <td className="py-1.5 text-gray-900 select-all">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-gray-400">Borrador para transcribir al facturador. No es un comprobante válido.</p>
      <div className="flex justify-end gap-2 flex-wrap">
        <button onClick={copiar} data-testid="fact-vista-copiar"
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"><Copy size={14} /> Copiar datos</button>
        <button onClick={() => facturaPdf(fila, empresa, true)} data-testid="fact-vista-preview"
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"><Eye size={14} /> Vista previa</button>
        <button onClick={() => facturaPdf(fila, empresa)} data-testid="fact-vista-pdf"
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#2E6DA4] text-white hover:bg-[#245A8A]"><Download size={14} /> PDF</button>
      </div>
    </Modal>
  );
}
